import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CalendarDaysIcon, ArrowDownTrayIcon, UserGroupIcon,
  ClockIcon, PlusIcon, XMarkIcon,
} from '@heroicons/react/24/outline';

const fmtTime = d => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}) : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const ini     = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'S';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .fgl * { box-sizing:border-box; }
  .fgl { font-family:'Sora',sans-serif; color:#1a1d2e; animation:fglFade .4s ease both; }
  @keyframes fglFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .fgl-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .fgl-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .fgl-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-teal  { background:#0d9488; color:white; } .btn-teal:hover { background:#0f766e; }
  .btn-amber { background:#fffbeb; color:#d97706; border:1px solid #fde68a; } .btn-amber:hover { background:#d97706; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .req-info { background:#f0fdfa; border:1px solid #99f6e4; border-radius:12px; padding:10px 16px; margin-bottom:1.2rem; font-size:.78rem; color:#134e4a; }
  .req-info strong { color:#0f766e; }
  .stat-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-icon { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .sc-icon svg { width:16px; height:16px; stroke-width:1.8; }
  .sc-icon.teal  { background:#f0fdfa; color:#0d9488; }
  .sc-icon.blue  { background:#eef2ff; color:#3b6ef8; }
  .sc-icon.amber { background:#fffbeb; color:#f59e0b; }
  .sc-lbl { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-val { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .fgl-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; align-items:center; margin-bottom:1.2rem; }
  .fgl-date { font-family:'JetBrains Mono',monospace; font-size:.8rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; outline:none; }
  .fgl-date:focus { border-color:#0d9488; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:10px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .name-cell { display:flex; align-items:center; gap:9px; }
  .avatar { width:32px; height:32px; border-radius:9px; background:linear-gradient(135deg,#0d9488,#3b6ef8); display:flex; align-items:center; justify-content:center; font-size:.65rem; font-weight:700; color:white; flex-shrink:0; }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .time-badge { display:inline-flex; align-items:center; gap:4px; font-family:'JetBrains Mono',monospace; font-size:.76rem; font-weight:600; padding:3px 9px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .time-badge svg { width:11px; height:11px; }
  .source-badge { display:inline-flex; font-size:.65rem; font-weight:600; padding:2px 8px; border-radius:99px; }
  .source-rfid   { background:#eef2ff; color:#3b6ef8; }
  .source-manual { background:#fffbeb; color:#f59e0b; }
  .empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  /* Mobile cards */
  .cards { display:none; padding:10px; }
  .card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .card-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .card-row { display:flex; justify-content:space-between; align-items:center; font-size:.75rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .card-row:last-of-type { border-bottom:none; }
  .card-lbl { color:#9094a8; font-weight:600; white-space:nowrap; }
  /* Modal */
  .overlay { position:fixed; inset:0; background:rgba(10,12,28,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; width:100%; max-width:440px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.2); animation:moIn .25s ease; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hdr { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .mo-title { font-size:.95rem; font-weight:700; color:#0f1123; }
  .mo-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-close svg { width:14px; height:14px; stroke-width:2; }
  .mo-body { padding:20px 22px; }
  .mo-foot { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; }
  .ff { display:flex; flex-direction:column; gap:5px; margin-bottom:12px; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .fi:focus,.fs:focus { border-color:#0d9488; background:white; }
  .fh { font-size:.68rem; color:#b0b5cc; }
  @media (max-width:768px) {
    .stat-grid { grid-template-columns:1fr 1fr; }
    .fgl-title  { font-size:1.1rem; }
    .tbl-wrap   { display:none !important; }
    .cards      { display:block; }
  }
`;

export default function FinanceStudentGateLog() {
  const [date,     setDate]     = useState(new Date().toISOString().slice(0,10));
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [modal,    setModal]    = useState(false);
  const [students, setStudents] = useState([]);
  const [form,     setForm]     = useState({ studentId:'', entryTime:'', note:'' });
  const [saving,   setSaving]   = useState(false);

  useEffect(() => { fetchLog(); }, [date]);
  useEffect(() => {
    api.get('/finance/classes').then(() => {}).catch(() => {});
    // Load students for manual entry modal — /admin/students and /coordinator/students
    // both 403 for finance users; finance's own biometric route returns the same shape.
    api.get('/finance/biometric/rfid-students').then(r => setStudents(r.data || [])).catch(() => {});
  }, []);

  const fetchLog = async () => {
    try {
      setLoading(true);
      const { data: res } = await api.get(`/finance/biometric/gate-log?date=${date}`);
      setData(res);
    } catch { toast.error('Failed to load gate log'); }
    finally   { setLoading(false); }
  };

  const submitRequest = async () => {
    if (!form.studentId) return toast.error('Select a student');
    setSaving(true);
    try {
      await api.post('/finance/biometric/request-manual-entry', form);
      toast.success('Manual entry request submitted — awaiting admin approval');
      setModal(false);
      setForm({ studentId:'', entryTime:'', note:'' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const exportCSV = () => {
    if (!data?.entries?.length) return toast.error('No data to export');
    const rows = data.entries.map(e => ({
      Name:          e.student?.user?.name || '',
      'Admission No':e.student?.admissionNo || '',
      Class:         e.student?.className || '',
      Section:       e.student?.section || '',
      'Entry Time':  fmtTime(e.entryTime),
      'Card UID':    e.cardUID,
      Source:        e.source,
    }));
    const hdrs = Object.keys(rows[0]);
    const csv  = [hdrs.join(','), ...rows.map(r => hdrs.map(h => r[h]).join(','))].join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href=url; a.download=`Gate_Log_${date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const entries        = data?.entries || [];
  const total          = data?.total   || 0;
  const uniqueStudents = data?.uniqueStudents || 0;
  const firstEntry     = entries[0]?.entryTime ? fmtTime(entries[0].entryTime) : '—';

  return (
    <div className="fgl"><style>{style}</style>

      <div className="fgl-hdr">
        <div>
          <div className="fgl-title">Student Gate Log</div>
          <div className="fgl-sub">RFID card entries — who entered school and when</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button className="btn btn-ghost btn-sm" onClick={exportCSV}><ArrowDownTrayIcon/>Export CSV</button>
          <button className="btn btn-amber btn-sm" onClick={() => { setModal(true); setForm({ studentId:'', entryTime: new Date().toISOString().slice(0,16), note:'' }); }}>
            <PlusIcon/>Request Manual Entry
          </button>
        </div>
      </div>

      {/* Request info notice */}
      <div className="req-info">
        <strong>Note:</strong> Manual entries require admin approval. RFID entries are logged automatically by the machine.
      </div>

      {/* Date picker */}
      <div className="fgl-toolbar">
        <input type="date" className="fgl-date" value={date} onChange={e => setDate(e.target.value)} />
        <span style={{fontSize:'.78rem',color:'#9094a8'}}>{fmtDate(date)}</span>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="sc-icon teal"><UserGroupIcon/></div>
          <div className="sc-lbl">Students Entered</div>
          <div className="sc-val">{uniqueStudents}</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon blue"><CalendarDaysIcon/></div>
          <div className="sc-lbl">Total Taps</div>
          <div className="sc-val">{total}</div>
        </div>
        <div className="stat-card">
          <div className="sc-icon amber"><ClockIcon/></div>
          <div className="sc-lbl">First Entry</div>
          <div className="sc-val" style={{fontSize:'1rem'}}>{firstEntry}</div>
        </div>
      </div>

      {/* Log table */}
      <div className="panel">
        <div className="panel-head">
          <div>
            <div className="panel-title">Entry Log — {fmtDate(date)}</div>
            <div className="panel-sub">{loading ? 'Loading…' : `${total} entries · ${uniqueStudents} unique students`}</div>
          </div>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"/>Loading…</div>
        ) : entries.length === 0 ? (
          <div className="empty">No entries for this date.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead><tr>
                  <th>#</th><th>Student</th><th>Admission No</th><th>Class</th><th>Section</th>
                  <th>Entry Time</th><th>Card UID</th><th>Source</th>
                </tr></thead>
                <tbody>
                  {entries.map((e,i) => (
                    <tr key={e._id}>
                      <td style={{color:'#c0c5dc',fontSize:'.72rem'}}>{i+1}</td>
                      <td>
                        <div className="name-cell">
                          <div className="avatar">{ini(e.student?.user?.name)}</div>
                          <div style={{fontWeight:600,color:'#0f1123'}}>{e.student?.user?.name||'—'}</div>
                        </div>
                      </td>
                      <td><span className="mono">{e.student?.admissionNo||'—'}</span></td>
                      <td><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{e.student?.className||'—'}</span></td>
                      <td><span style={{fontSize:'.74rem',background:'#eef2ff',color:'#3b6ef8',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{e.student?.section||'—'}</span></td>
                      <td><span className="time-badge"><ClockIcon/>{fmtTime(e.entryTime)}</span></td>
                      <td><span className="mono">{e.cardUID}</span></td>
                      <td><span className={`source-badge source-${e.source}`}>{e.source}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="cards">
              {entries.map((e,i) => (
                <div key={e._id} className="card">
                  <div className="card-top">
                    <div className="name-cell">
                      <div className="avatar">{ini(e.student?.user?.name)}</div>
                      <div>
                        <div style={{fontWeight:600,color:'#0f1123',fontSize:'.82rem'}}>{e.student?.user?.name}</div>
                        <div style={{fontSize:'.68rem',color:'#9094a8'}}>{e.student?.admissionNo}</div>
                      </div>
                    </div>
                    <span className="time-badge"><ClockIcon/>{fmtTime(e.entryTime)}</span>
                  </div>
                  <div className="card-row"><span className="card-lbl">Class</span><span style={{fontSize:'.74rem',background:'#f3f0ff',color:'#8b5cf6',padding:'2px 8px',borderRadius:99,fontWeight:600}}>{e.student?.className||'—'} {e.student?.section}</span></div>
                  <div className="card-row"><span className="card-lbl">Card UID</span><span className="mono">{e.cardUID}</span></div>
                  <div className="card-row"><span className="card-lbl">Source</span><span className={`source-badge source-${e.source}`}>{e.source}</span></div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Manual entry request modal */}
      {modal && createPortal(
        <div className="overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="mo-hdr">
              <div className="mo-title">Request Manual Gate Entry</div>
              <button className="mo-close" onClick={() => setModal(false)}><XMarkIcon/></button>
            </div>
            <div className="mo-body">
              <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:9,padding:'10px 12px',fontSize:'.75rem',color:'#92400e',marginBottom:14}}>
                ⚠️ This request will be sent to admin for approval before the entry is logged.
              </div>
              <div className="ff">
                <label className="fl">Student *</label>
                <select className="fs" value={form.studentId} onChange={e => setForm(p=>({...p,studentId:e.target.value}))}>
                  <option value="">Select student…</option>
                  {students.map(s => <option key={s._id} value={s._id}>{s.user?.name} ({s.admissionNo})</option>)}
                </select>
              </div>
              <div className="ff">
                <label className="fl">Entry Time (optional)</label>
                <input type="datetime-local" className="fi" value={form.entryTime} onChange={e => setForm(p=>({...p,entryTime:e.target.value}))} />
                <span className="fh">Leave blank to use current time when admin approves</span>
              </div>
              <div className="ff">
                <label className="fl">Reason / Note</label>
                <input className="fi" value={form.note} onChange={e => setForm(p=>({...p,note:e.target.value}))} placeholder="Why is this manual entry needed?" />
              </div>
            </div>
            <div className="mo-foot">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-amber" onClick={submitRequest} disabled={saving}>
                {saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}